import React from 'react'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import type { MarkElementProps } from '@mui/x-charts'

import { MARKER_SHAPE_PATHS } from './utils/markerShapes'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'

export type MarkHandlers = {
  onMarkClick?: (event: React.MouseEvent<SVGPathElement, MouseEvent>, dataIndex: number) => void
  onMouseEnter?: (dataIndex: number) => void
  onMouseLeave?: () => void
}

interface CustomMarkProps extends MarkElementProps {
  handlers?: MarkHandlers
  showLabel: boolean
  showMark?: boolean
  yData: number[]
  style?: React.CSSProperties & {
    markLabelStyles?: React.CSSProperties
  }
  labelBackgroundColor?: string
  labelFontColor?: string
  labelFontSize?: number
  valueFormatterString?: string
}

export const CustomMark: React.FC<CustomMarkProps> = (props) => {
  const theme = useTheme()
  const showLabel = props.showLabel
  const styles = props.style
  const markLabelStyles = styles?.markLabelStyles
  const xNumber = Number(props.x)
  const yNumber = Number(props.y)

  const handleMarkClick = props.handlers?.onMarkClick
  const handleMouseEnter = props.handlers?.onMouseEnter
  const handleMouseLeave = props.handlers?.onMouseLeave

  const isSmallScreenUp = useMediaQuery(theme.breakpoints.up('sm'))

  // Calculate text dimensions for proper background sizing
  const formattedValue = props.valueFormatterString
    ? formatCurrency(props.yData[props.dataIndex], props.valueFormatterString)
    : String(props.yData[props.dataIndex])
  const fontSize = isSmallScreenUp
    ? theme.typography.body2?.fontSize
    : theme.typography.caption?.fontSize
  const rectWidth = 30
  const rectHeight = 24

  // Center the rectangle around the text
  const rectX = xNumber - rectWidth / 2
  const rectY = yNumber - 30
  return (
    <>
      {showLabel && (
        <>
          {props.labelBackgroundColor && (
            <rect
              fill={props.labelBackgroundColor}
              height={rectHeight}
              rx="3"
              width={rectWidth}
              x={rectX}
              y={rectY}
            />
          )}

          <text
            className="MuiLineChart-markLabel"
            dominantBaseline="middle"
            fill={props.labelFontColor ? props.labelFontColor : theme.palette.text.primary}
            style={{
              fontSize: props.labelFontSize ? props.labelFontSize : fontSize,
              ...markLabelStyles,
            }}
            textAnchor="middle"
            x={xNumber}
            y={yNumber - 28 + rectHeight / 2}
          >
            {formattedValue}
          </text>
        </>
      )}
      {props.showMark && (
        <path
          cursor={handleMarkClick ? 'pointer' : 'unset'}
          d={MARKER_SHAPE_PATHS[props.shape ?? 'circle']}
          height="20px"
          onClick={(event) => {
            handleMarkClick?.(event, props.dataIndex)
          }}
          onMouseEnter={() => {
            handleMouseEnter?.(props.dataIndex)
          }}
          onMouseLeave={() => {
            handleMouseLeave?.()
          }}
          style={{
            fill: styles?.fill ? styles.fill : theme.palette.background.paper,
            stroke: styles?.stroke ? styles.stroke : theme.palette.primary.main,
            strokeWidth: 2,
            transform: `translate(${props.x}px, ${props.y}px)`,
            transformOrigin: `${props.x}px, ${props.y}px`,
            filter: styles?.filter ? styles.filter : 'none',
          }}
          width="20px"
        />
      )}
    </>
  )
}
